import React from "react";
import { View, ScrollView, TouchableWithoutFeedback } from "react-native";
import Svg, { Path } from "react-native-svg";
import ENV from "app/utils/ENV";
import Text from "app/components/Text";
import RichText from "app/components/RichText";
import TextInput from "app/components/TextInput";
import MomentComposer from "app/components/chapter/MomentComposer";
import Tag from "app/components/chapter/Tag";
import valueActions from "app/utils/valueActions";
import valueHelpers from "app/utils/valueHelpers";
import colorHelpers from "app/utils/colorHelpers";
import store from "app/redux/store";
import { add as addValuesToStore } from "app/actions/values";
import { StyleContext } from "app/contexts/Style";

// Condensed hero's journey (Campbell): the value's story beats. The arc dips
// through the middle beats — the descent into the unknown — and rises back.
export const JOURNEY_BEATS = [
  { key: "ordinary", title: "Ordinary World", hint: "Where did this begin?" },
  { key: "call", title: "The Call", hint: "What set it in motion?" },
  { key: "threshold", title: "The Threshold", hint: "When did you commit?" },
  { key: "trials", title: "Trials & Allies", hint: "What tested you? Who helped?" },
  { key: "ordeal", title: "The Ordeal", hint: "The hardest moment" },
  { key: "reward", title: "The Reward", hint: "What was gained?" },
  { key: "roadback", title: "The Road Back", hint: "Turning home, changed" },
  { key: "elixir", title: "Return with the Elixir", hint: "What do you bring back — and what does it seed?" },
];

// the full 12-stage Campbell cycle; shares keys with the condensed set so
// beat text survives toggling between them
export const JOURNEY_BEATS_FULL = [
  { key: "ordinary", title: "Ordinary World", hint: "Where did this begin?" },
  { key: "call", title: "The Call", hint: "What set it in motion?" },
  { key: "refusal", title: "Refusal of the Call", hint: "What made you hesitate?" },
  { key: "mentor", title: "Meeting the Mentor", hint: "Who or what gave you the push?" },
  { key: "threshold", title: "The Threshold", hint: "When did you commit?" },
  { key: "trials", title: "Trials & Allies", hint: "What tested you? Who helped?" },
  { key: "approach", title: "The Approach", hint: "Preparing for the worst of it" },
  { key: "ordeal", title: "The Ordeal", hint: "The hardest moment" },
  { key: "reward", title: "The Reward", hint: "What was gained?" },
  { key: "roadback", title: "The Road Back", hint: "Turning home, changed" },
  { key: "resurrection", title: "Resurrection", hint: "The final test — is the change real?" },
  { key: "elixir", title: "Return with the Elixir", hint: "What do you bring back — and what does it seed?" },
];

export default class JourneyMap extends React.Component {
  static contextType = StyleContext;

  constructor(props) {
    super(props);
    let beats = {};
    let journeyTitle = "";
    let allSteps = false;
    let spiral = false;
    try {
      let parsed = JSON.parse(props.value?.journey || "{}") || {};
      beats = parsed.beats || {};
      journeyTitle = parsed.title || "";
      allSteps = !!parsed.allSteps;
      spiral = !!parsed.spiral;
    } catch (e) {
      console.warn("JourneyMap: could not parse journey", e);
    }
    // how far the middle beats sink into the unknown world
    this.ARC_DEPTH = props.mini ? 70 : 260;
    // remember the stored prefs so mini-mode saves don't clobber them
    this.storedPrefs = { allSteps, spiral };
    if (props.mini) {
      // mini map: always the line view; 8 steps unless one of the granular
      // stages (refusal, mentor, approach, resurrection) holds content
      spiral = false;
      let hiddenKeys = JOURNEY_BEATS_FULL.filter(
        (f) => !JOURNEY_BEATS.find((c) => c.key == f.key),
      ).map((f) => f.key);
      allSteps = hiddenKeys.some(
        (k) => !!beats[k]?.text?.length || !!beats[k]?.value_ids?.length,
      );
    }
    this.state = { beats, journeyTitle, allSteps, spiral, focusedBeatKey: null };
  }

  componentDidMount() {
    this.ensureLinkedValuesLoaded();
  }

  componentWillUnmount() {
    this._journeyUnmounted = true;
    // flush a pending debounced save so toggling away doesn't drop edits
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveJourney();
    }
  }

  // Beat chips render from the redux store, but the boot index only loads
  // the newest revision per value lineage — a linked older revision (or
  // anything else outside that scope) would silently drop its chip. Fetch
  // any missing linked ids explicitly.
  ensureLinkedValuesLoaded = async () => {
    let { beats } = this.state;
    let values = store.getState().values;
    let missing = [];
    Object.keys(beats).forEach((key) =>
      (beats[key]?.value_ids || []).forEach((id) => {
        if (!values.find((v) => v.id == id) && !missing.includes(id))
          missing.push(id);
      }),
    );
    if (!missing.length) return;
    await valueActions.indexFromIds(missing);
    if (!this._journeyUnmounted) this.forceUpdate();
  };

  updateBeat = (key, patch, saveDelay = 1200) => {
    let beats = {
      ...this.state.beats,
      [key]: { ...(this.state.beats[key] || {}), ...patch },
    };
    this.setState({ beats });
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(this.saveJourney, saveDelay);
  };

  updateTitle = (journeyTitle) => {
    this.setState({ journeyTitle });
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(this.saveJourney, 1200);
  };

  saveJourney = async () => {
    this.saveTimeout = null;
    let { value } = this.props;
    if (!value?.id) return;
    let journey = JSON.stringify({
      title: this.state.journeyTitle || undefined,
      // mini mode forces its own view; persist the dash's stored prefs
      allSteps:
        (this.props.mini ? this.storedPrefs.allSteps : this.state.allSteps) ||
        undefined,
      spiral:
        (this.props.mini ? this.storedPrefs.spiral : this.state.spiral) ||
        undefined,
      beats: this.state.beats,
    });
    let res = await valueActions.update(value.id, { journey });
    // keep the parent's copy of the value fresh so remounting (toggling
    // Moments/Narrative) doesn't resurrect stale beats
    if (!!res && !res.conflict) {
      this.props.onUpdateJourney && this.props.onUpdateJourney(journey);
    }
  };

  // pinning an EXISTING value to a beat records the reference only — no
  // relationship is created (values created from the beat composer are
  // still adopted as children by the create flow)
  handlePinToBeat = async (key, item) => {
    if (!item?.id) return;
    this.linkValueToBeat(key, item);
  };

  // a created or pinned value becomes a child of the viewed value (the
  // composer handles that) — here we remember which beat it belongs to.
  // Links are additive: the beat's prose text is left untouched.
  linkValueToBeat = (key, item) => {
    if (!item?.id) return;
    // the picked suggestion may not be in the local store (old revisions
    // etc. aren't index-loaded) — seed it so the chip renders immediately
    if (!store.getState().values.find((v) => v.id == item.id)) {
      store.dispatch(addValuesToStore([item]));
    }
    let beat = this.state.beats[key] || {};
    let value_ids = [...(beat.value_ids || [])];
    if (!value_ids.includes(item.id)) value_ids.push(item.id);
    this.updateBeat(key, { value_ids }, 0);
  };

  unlinkValueFromBeat = (key, item) => {
    let beat = this.state.beats[key] || {};
    let value_ids = (beat.value_ids || []).filter((id) => id != item.id);
    this.updateBeat(key, { value_ids }, 0);
  };

  // alt+left/right at the edge of a beat's prose jumps to the neighbor beat
  handleBeatAltArrow = (i, dir) => {
    let ref = this[`beat-input-${i}`];
    let text = (this.state.beats[this.beatDefs()[i]?.key] || {}).text || "";
    if (dir < 0) {
      // only jump when the caret sits at the very start; otherwise let
      // alt+left do its native word-jump
      if (ref?.selectionStart != null && ref.selectionStart !== 0) return false;
      this.focusBeat(i - 1, true);
    } else {
      if (ref?.selectionEnd != null && ref.selectionEnd !== text.length)
        return false;
      this.focusBeat(i + 1, false);
    }
    return true;
  };

  focusBeatComposer = (i) => {
    let ref = this[`beat-composer-${i}`];
    ref && ref.focus && ref.focus();
  };

  // clear a focus key after a short grace period — an immediate clear would
  // unmount the composer mid-click (blur fires before the click lands)
  blurBeatSoon = (key, field) => {
    setTimeout(() => {
      if (this._journeyUnmounted) return;
      this.setState((s) => (s[field] == key ? { [field]: null } : null));
    }, 150);
  };

  focusBeat = async (i, caretAtEnd = false) => {
    if (i < 0 || i >= this.beatDefs().length) return;
    // a blurred beat renders as RichText — mount its editor first
    await this.setState({ focusedBeatKey: this.beatDefs()[i].key });
    let text = (this.state.beats[this.beatDefs()[i].key] || {}).text || "";
    let pos = caretAtEnd ? text.length : 0;
    setTimeout(() => {
      let ref = this[`beat-input-${i}`];
      if (!ref) return;
      ref.focus && ref.focus();
      try {
        if (ref.setSelectionRange) ref.setSelectionRange(pos, pos);
        else if (ref.setNativeProps)
          ref.setNativeProps({ selection: { start: pos, end: pos } });
      } catch (e) {}
    }, 0);
    // keep the targeted card in view (86 = the world-label gutter);
    // spiral mode positions cards absolutely, so linear math doesn't apply
    if (!this.state.spiral) {
      this.scrollRef?.scrollTo &&
        this.scrollRef.scrollTo({
          x: Math.max(0, 86 + i * (250 + this.context.sizes.base) - 80),
          animated: true,
        });
    }
  };

  // ARC_DEPTH is set in the constructor: deep for the full dash map, shallow
  // for the mini map under expanded bullets

  // the dashed ordinary/unknown boundary is pinned to The Threshold beat:
  // its card straddles the line, everything before it sits above
  thresholdLineY = () => {
    let defs = this.beatDefs();
    let ti = defs.findIndex((b) => b.key == "threshold");
    if (ti < 0) ti = Math.floor(defs.length / 4);
    return this.arcOffset(ti) + (this.props.mini ? 18 : 30);
  };

  // progression phase 0→1 anchored so THE ORDEAL is always the nadir
  // (phase 0.5) regardless of how many beats sit on either side of it.
  // Accepts fractional i so the spiral path can be sampled continuously.
  beatPhase = (i) => {
    let defs = this.beatDefs();
    let nadir = defs.findIndex((b) => b.key == "ordeal");
    if (nadir <= 0) return defs.length > 1 ? i / (defs.length - 1) : 0;
    if (i <= nadir) return (i / nadir) * 0.5;
    return 0.5 + ((i - nadir) / (defs.length - 1 - nadir)) * 0.5;
  };

  arcOffset = (i) => {
    // 0 at the ends, sinking to the ordeal: a triangular profile with a
    // soft knee (tri^0.7) — a pure sine flattened to a ~7px step between
    // the beats adjacent to the ordeal, this keeps every step substantial
    let phase = this.beatPhase(i);
    let tri = 1 - Math.abs(1 - 2 * phase);
    return Math.pow(tri, 0.7) * this.ARC_DEPTH;
  };

  // which beat set is active (condensed 8 vs the full 12 stages)
  beatDefs = () => (this.state.allSteps ? JOURNEY_BEATS_FULL : JOURNEY_BEATS);

  toggleAllSteps = () => {
    this.setState({ allSteps: !this.state.allSteps }, () => {
      if (this.saveTimeout) clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(this.saveJourney, 300);
    });
  };

  toggleSpiral = () => {
    this.setState({ spiral: !this.state.spiral }, () => {
      if (this.saveTimeout) clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(this.saveJourney, 300);
    });
  };

  // spiral geometry: a full revolution where the last beat returns to the
  // starting angle at a larger radius — the return sits above the departure
  spiralLayout = () => {
    let n = this.beatDefs().length;
    let cardW = 210;
    let GROWTH = 140;
    // the ring doesn't quite close: the first beat starts just right of the
    // top and the last ends just left of it, so the return doesn't cover
    // the departure (it sits above and to the left). Asymmetric: a smaller
    // leading trim keeps beat 1 near the top, clear of beat 2.
    let SPLIT_START = 0.12; // radians (~7°) before the first beat
    let SPLIT_END = 0.24; // radians (~14°) after the last beat
    // radius from the TIGHTEST angular gap between adjacent beats (the
    // ordeal-anchored phases aren't uniform), so neighboring cards at the
    // top/bottom of the ring always clear each other horizontally
    let minGap = 1;
    for (let i = 0; i < n - 1; i++) {
      minGap = Math.min(minGap, this.beatPhase(i + 1) - this.beatPhase(i));
    }
    let R0 = Math.max(300, Math.ceil((cardW + 35) / (2 * Math.sin(minGap * Math.PI))));
    let pad = 130; // room for cards + labels around the ring
    let size = 2 * (R0 + GROWTH) + pad * 2;
    let cx = size / 2;
    let cy = size / 2;
    // a point on the spiral for a (possibly fractional) beat index: start at
    // the top, clockwise, the ordeal at the bottom, radius growing so the
    // return lands above the departure
    let pointAt = (fi) => {
      let phase = this.beatPhase(fi);
      let angle =
        -Math.PI / 2 +
        SPLIT_START +
        phase * (Math.PI * 2 - SPLIT_START - SPLIT_END);
      let r = R0 + phase * GROWTH;
      return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
    };
    let positions = [];
    for (let i = 0; i < n; i++) {
      let p = pointAt(i);
      positions.push({ left: p.x - cardW / 2, top: p.y - 80 });
    }
    // continuous path along the spiral, connecting the beats in order
    let steps = 96;
    let pathD = "";
    for (let s = 0; s <= steps; s++) {
      let p = pointAt((s / steps) * (n - 1));
      pathD += `${s ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)} `;
    }
    return { size, cx, cy, cardW, positions, pathD };
  };

  renderToggles = () => {
    let t = (active, label, onPress, keySuffix) => (
      <Text
        key={keySuffix}
        smallButton
        selectedButton={active}
        style={{ marginLeft: this.context.sizes.tiny }}
        onPress={onPress}
      >
        {label}
      </Text>
    );
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "baseline",
          justifyContent: "flex-end",
          flex: 1,
        }}
      >
        {t(!this.state.allSteps, "8 steps", this.toggleAllSteps, "s8")}
        {t(!!this.state.allSteps, "All steps", this.toggleAllSteps, "s12")}
        <Text trim style={{ marginLeft: this.context.sizes.small }}>
          ·
        </Text>
        {t(!this.state.spiral, "Line", this.toggleSpiral, "line")}
        {t(!!this.state.spiral, "Spiral", this.toggleSpiral, "spiral")}
      </View>
    );
  };

  renderBeatCard = (b, i, positionStyle = null) => {
    let { value } = this.props;
    let minLines = this.props.mini ? 2 : 3;
    let maxLines = this.props.mini ? 4 : 5;
    let { beats } = this.state;
    let values = store.getState().values;
    let color =
      valueHelpers.getColor(value, this.context.colorScheme) ||
      this.context.colors.foreground;
    let beat = beats[b.key] || {};
    let linked = (beat.value_ids || [])
      .map((id) => values.find((v) => v.id == id))
      .filter((v) => !!v);
    let filled = !!beat.text?.length || !!linked.length;
    return (
      <View
        key={b.key}
        style={[
          this.styles().beatCard,
          this.props.mini && { width: 190, padding: this.context.sizes.small },
          filled && {
            borderColor: colorHelpers.hexToRgbaString(color, 0.6),
          },
          positionStyle,
        ]}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text
            style={[
              this.styles().beatNumber,
              filled && {
                backgroundColor: color,
                color: this.context.colors.empty,
              },
            ]}
          >
            {i + 1}
          </Text>
          <Text bold numberOfLines={1} style={{ flex: 1 }}>
            {b.title}
          </Text>
        </View>

        {!!linked.length && (
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
            {linked.map((v) => (
              <Tag
                key={v.id}
                small
                value={v}
                onPress={() => this.unlinkValueFromBeat(b.key, v)}
              />
            ))}
          </View>
        )}

        {/* the beat's prose: rendered rich text when blurred (like a
            scratchpad), a plain editor while focused or empty */}
        {this.state.focusedBeatKey != b.key && !!beat.text?.length ? (
          <TouchableWithoutFeedback onPress={() => this.focusBeat(i, true)}>
            <View
              style={{
                minHeight: this.context.sizes.default * 1.4 * minLines,
                maxHeight: this.context.sizes.default * 1.4 * maxLines,
                paddingVertical: this.context.sizes.small,
                cursor: "text",
              }}
            >
              {/* overflowing beats scroll even while collapsed */}
              <ScrollView
                nestedScrollEnabled
                showsVerticalScrollIndicator={false}
              >
                <RichText
                  color={this.context.colors.foreground}
                  value={value}
                  lineHeightRatio={1.4}
                >
                  {beat.text}
                </RichText>
              </ScrollView>
            </View>
          </TouchableWithoutFeedback>
        ) : (
          <TextInput
            multiline
            autoHeight
            autoFocus={this.state.focusedBeatKey == b.key}
            value={beat.text || ""}
            placeholder={b.hint}
            inputRef={(r) => (this[`beat-input-${i}`] = r)}
            onAltLeft={() => this.handleBeatAltArrow(i, -1)}
            onAltRight={() => this.handleBeatAltArrow(i, 1)}
            // cmd+←/→ at the text edges also jump beats (mid-text they keep
            // their native line-start/line-end behavior)
            onMetaLeft={() => this.handleBeatAltArrow(i, -1)}
            onMetaRight={() => this.handleBeatAltArrow(i, 1)}
            // ↓ at the end of the prose drops into the composer;
            // ↑ at the very start climbs to the previous beat's prose
            focusNext={() => this.focusBeatComposer(i)}
            focusPrevious={() => this.focusBeat(i - 1, true)}
            onFocus={() => this.setState({ focusedBeatKey: b.key })}
            onBlur={() => this.blurBeatSoon(b.key, "focusedBeatKey")}
            onChangeText={(text) => this.updateBeat(b.key, { text })}
            style={{
              fontSize: this.context.sizes.default,
              // match the blurred RichText's line-height so focusing a
              // beat doesn't reflow the text
              lineHeight: this.context.sizes.default * 1.4,
              color: this.context.colors.foreground,
              minHeight: this.context.sizes.default * 1.4 * minLines,
              // cap so long beats scroll internally instead of growing the
              // card (and pushing the timeline offscreen)
              maxHeight: this.context.sizes.default * 1.4 * maxLines,
              borderWidth: 0,
              paddingVertical: this.context.sizes.small,
            }}
          />
        )}

        {/* secondary: link or create a child value for this beat — only
            shown while the beat (prose or composer) is focused */}
        {(this.state.focusedBeatKey == b.key ||
          this.state.focusedComposerKey == b.key) && (
          <View
            style={{
              borderTopWidth: 1,
              borderColor: colorHelpers.hexToRgbaString(
                this.context.colors.trim,
                0.5,
              ),
              paddingTop: this.context.sizes.tiny,
            }}
          >
            <MomentComposer
              key={`${value.id}-${b.key}`}
              noIcon
              inputRef={(r) => (this[`beat-composer-${i}`] = r)}
              // ↑ from the (empty) composer returns to the prose;
              // ↓ moves on to the next beat's prose
              focusPrevious={() => this.focusBeat(i, true)}
              focusNext={() => this.focusBeat(i + 1, false)}
              placeholder={"Link or add a value…"}
              placeholderTextColor={colorHelpers.hexToRgbaString(
                this.context.colors.trim,
                0.8,
              )}
              parentIsValue
              parentId={value.id}
              rootChapter={{ id: `value-${value.id}`, is_value: true }}
              onAddChild={(child) => this.linkValueToBeat(b.key, child)}
              onPinValue={(item) => this.handlePinToBeat(b.key, item)}
              onRefreshChapter={this.props.onRefreshChapter}
              onFocus={() => this.setState({ focusedComposerKey: b.key })}
              onBlur={() => this.blurBeatSoon(b.key, "focusedComposerKey")}
            />
          </View>
        )}
      </View>
    );
  };

  renderLinear = () => {
    let defs = this.beatDefs();
    let thresholdY = this.thresholdLineY();
    return (
      <ScrollView
        horizontal
        ref={(r) => (this.scrollRef = r)}
        showsHorizontalScrollIndicator={ENV.WEB}
        // bounded so the timeline below the map always stays on screen
        style={{
          paddingVertical: this.context.sizes.base,
          maxHeight: this.props.mini ? 340 : 760,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            paddingLeft: this.props.mini ? this.context.sizes.small : 86,
          }}
        >
          {/* the threshold: ordinary world above, the unknown below — end
              beats start above the line, the descent dips beneath it
              (skipped in the mini map to keep it quiet) */}
          {!this.props.mini && (
            <View
              pointerEvents="none"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: thresholdY,
                borderTopWidth: 1,
                borderStyle: "dashed",
                borderColor: this.context.colors.trim,
              }}
            />
          )}
          {!this.props.mini && (
            <Text
              pointerEvents="none"
              // 2 lines × 12px line-height + a 6px gap keeps it fully above
              style={[this.styles().worldLabel, { top: thresholdY - 30 }]}
            >
              ORDINARY{"\n"}WORLD
            </Text>
          )}
          {!this.props.mini && (
            <Text
              pointerEvents="none"
              style={[this.styles().worldLabel, { top: thresholdY + 6 }]}
            >
              THE{"\n"}UNKNOWN
            </Text>
          )}
          {defs.map((b, i) =>
            this.renderBeatCard(b, i, { marginTop: this.arcOffset(i) }),
          )}
        </View>
      </ScrollView>
    );
  };

  renderSpiral = () => {
    let defs = this.beatDefs();
    let { size, cy, cardW, positions, pathD } = this.spiralLayout();
    return (
      <ScrollView
        horizontal
        ref={(r) => (this.scrollRef = r)}
        showsHorizontalScrollIndicator={ENV.WEB}
        style={{ paddingVertical: this.context.sizes.base }}
      >
        <View style={{ width: size, height: size }}>
          {/* the spiral's own line, connecting the beats in order */}
          <Svg
            pointerEvents="none"
            width={size}
            height={size}
            style={{ position: "absolute", left: 0, top: 0 }}
          >
            <Path
              d={pathD}
              fill="none"
              stroke={colorHelpers.hexToRgbaString(
                this.context.colors.trim,
                0.9,
              )}
              strokeWidth={1.5}
            />
          </Svg>
          {/* the threshold bisects the ring: ordinary world above, the
              unknown below */}
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: cy,
              borderTopWidth: 1,
              borderStyle: "dashed",
              borderColor: this.context.colors.trim,
            }}
          />
          <Text
            pointerEvents="none"
            style={[this.styles().worldLabel, { top: cy - 18 }]}
          >
            ORDINARY{"\n"}WORLD
          </Text>
          <Text
            pointerEvents="none"
            style={[this.styles().worldLabel, { top: cy + 6 }]}
          >
            THE{"\n"}UNKNOWN
          </Text>
          {defs.map((b, i) =>
            this.renderBeatCard(b, i, {
              position: "absolute",
              left: positions[i].left,
              top: positions[i].top,
              width: cardW,
              marginRight: 0,
              // later beats stack above earlier ones, so the return with
              // the elixir sits on top of the ordinary world it left
              zIndex: i + 1,
            }),
          )}
        </View>
      </ScrollView>
    );
  };

  render() {
    if (this.props.mini) {
      // compact map under an expanded bullet: editable title, no toggles
      return (
        <View>
          <TextInput
            value={this.state.journeyTitle}
            placeholder={"Title this journey…"}
            placeholderTextColor={colorHelpers.hexToRgbaString(
              this.context.colors.trim,
              0.9,
            )}
            onChangeText={this.updateTitle}
            style={{
              fontSize: this.context.sizes.default * 1.15,
              fontWeight: "bold",
              fontStyle: this.state.journeyTitle ? "normal" : "italic",
              color: this.context.colors.foreground,
              borderWidth: 0,
              paddingHorizontal: this.context.sizes.small,
              maxWidth: 480,
            }}
          />
          {this.renderLinear()}
        </View>
      );
    }
    return (
      <View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "baseline",
            marginBottom: this.context.sizes.small,
          }}
        >
          <TextInput
            value={this.state.journeyTitle}
            placeholder={"Title this journey… (optional)"}
            placeholderTextColor={colorHelpers.hexToRgbaString(
              this.context.colors.trim,
              0.9,
            )}
            onChangeText={this.updateTitle}
            style={{
              fontSize: this.context.sizes.default * 1.2,
              fontWeight: "bold",
              fontStyle: this.state.journeyTitle ? "normal" : "italic",
              color: this.context.colors.foreground,
              borderWidth: 0,
              paddingHorizontal: this.context.sizes.small,
              flex: 1,
              maxWidth: 720,
            }}
          />
          {this.renderToggles()}
        </View>
        {this.state.spiral ? this.renderSpiral() : this.renderLinear()}
      </View>
    );
  }

  styles = () => ({
    beatCard: {
      width: 250,
      marginRight: this.context.sizes.base,
      padding: this.context.sizes.base,
      borderWidth: 1,
      borderColor: this.context.colors.trim,
      borderRadius: this.context.sizes.borderRadius,
      backgroundColor: this.context.colors.empty,
    },
    worldLabel: {
      position: "absolute",
      left: 0,
      width: 80,
      fontSize: 9,
      letterSpacing: 1.5,
      lineHeight: 12,
      color: this.context.colors.trim,
    },
    beatNumber: {
      width: 22,
      height: 22,
      lineHeight: 22,
      textAlign: "center",
      borderRadius: 11,
      overflow: "hidden",
      marginRight: this.context.sizes.small,
      backgroundColor: this.context.colors.trim,
      fontSize: this.context.sizes.smallFont,
    },
  });
}
